import {
  type ComponentType,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import type { ComposerFile } from "src/components/llm/utils/file-processing";
import type { ComposerLeftAction } from "./composer-left-actions";

/**
 * An image/file source a chat composer can offer beyond the always-present file
 * upload — e.g. the content-library picker or the Ad Studio catalog selector.
 *
 * A source is self-contained: `usePicker` owns its own modal and state, and
 * resolves picked items to `ComposerFile[]`. The composer supplies the callback
 * that appends those files to its own file state, renders the picker's
 * `element` somewhere stable, and builds a toolbar button from `label`/`icon`.
 * The composer never needs to know what the source *is* (content library,
 * catalog, …) — only how to collect its output. Callers declare the sources a
 * surface offers; nothing about a specific product leaks into the composer.
 */
export type AttachmentSource = {
  /** Stable identity for the toolbar action and React keys. */
  key: string;
  /** Row label in the "+" menu; tooltip/aria-label when rendered inline. */
  label: string;
  icon: ComponentType;
  /**
   * Imperative picker: `open()` shows it, `element` mounts it. Called once per
   * source by a `SourcePickerHost` (a component, so the hook count is fixed even
   * when the source list varies). `onSelect` receives the picked files.
   */
  usePicker: (args: { onSelect: (files: ComposerFile[]) => void }) => {
    open: () => void;
    element: ReactNode;
  };
};

/**
 * Wraps a single `AttachmentSource`'s `usePicker` in a component so a composer
 * can render a *dynamic* list of sources without breaking the rules of hooks —
 * each host calls exactly one hook, and the list is a keyed set of the same
 * component. The host renders the picker's `element` and registers a *stable*
 * opener up via `onReady` so the parent can drive it from the toolbar; the
 * opener is cleared on unmount so it can't be invoked after the source leaves
 * the list.
 *
 * Pickers commonly return a fresh `open` closure each render (e.g.
 * `() => setIsOpen(true)`), so we register a stable wrapper that reads the
 * latest `open` from a ref rather than re-registering every render — which would
 * loop through the parent's `onReady` state update.
 */
export const SourcePickerHost = ({
  source,
  onSelect,
  onReady,
}: {
  source: AttachmentSource;
  onSelect: (files: ComposerFile[]) => void;
  onReady: (key: string, open: (() => void) | null) => void;
}): ReactElement => {
  const { open, element } = source.usePicker({ onSelect });

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const stableOpen = () => openRef.current();
    onReady(source.key, stableOpen);
    return () => onReady(source.key, null);
  }, [source.key, onReady]);

  return <>{element}</>;
};

/**
 * Wires a composer's declared `attachmentSources` into the pieces it needs:
 * toolbar `actions` (folded into the "+" menu alongside file upload) and
 * `elements` (the pickers' modals, which the composer must render somewhere
 * stable — outside the lazy menu — so an in-flight selection survives the menu
 * closing).
 *
 * The composer owns file state; `onSelect` is its own file-adding callback, so
 * every source funnels into the same state as file upload / paste / drop. A
 * source whose `open` handler hasn't registered yet (first render) is disabled
 * rather than firing a no-op.
 */
export const useAttachmentSources = ({
  sources,
  onSelect,
  isDisabled,
}: {
  sources: AttachmentSource[];
  onSelect: (files: ComposerFile[]) => void;
  isDisabled?: boolean;
}): { actions: ComposerLeftAction[]; elements: ReactNode } => {
  const [openHandlers, setOpenHandlers] = useState<Map<string, () => void>>(
    new Map(),
  );

  const handleReady = useCallback((key: string, open: (() => void) | null) => {
    setOpenHandlers((prev) => {
      const next = new Map(prev);
      if (open) {
        next.set(key, open);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  const actions: ComposerLeftAction[] = sources.map((source) => {
    const open = openHandlers.get(source.key);
    return {
      key: source.key,
      label: source.label,
      icon: source.icon,
      onClick: () => open?.(),
      isDisabled: isDisabled || !open,
    };
  });

  const elements = sources.map((source) => (
    <SourcePickerHost
      key={source.key}
      source={source}
      onSelect={onSelect}
      onReady={handleReady}
    />
  ));

  return { actions, elements };
};
